"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import { useEffect } from "react";
/**
 * The fleet's shared theme boundary. Mirror the theme SETTING + active locale
 * into the SHARED owlcat-theme / owlcat-locale cookies on the PARENT domain so
 * the central accounts app and sibling owlcat apps inherit light/dark + language
 * on redirect.
 *
 * We write the SETTING (`theme`: system|light|dark), NOT resolvedTheme: a
 * 'system' user must CLEAR the shared cookie so sibling apps also fall back to
 * the OS, instead of being pinned to a concrete light/dark that stops tracking
 * prefers-color-scheme cross-app. First visit respects the OS
 * (defaultTheme='system' + enableSystem); an explicit choice propagates cross-app
 * via the cookie, and the pre-hydration seed script (THEME_SEED, inlined in
 * app/layout.tsx) reads it back into localStorage['theme'] so next-themes
 * hydrates in agreement (no reset, no flash). Host-only on bare localhost
 * (NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN unset), Domain-scoped behind Caddy.
 *
 * `locales` is the caller's accepted locale list (validated before the
 * owlcat-locale cookie is written); defaults to the six fleet locales.
 */
/** The six owlcat fleet locales — default for callers that omit `locales`. */
export const FLEET_LOCALES = ["ru", "en", "cs", "uk", "de", "fr"];
export function ThemeCookieSync({ locales = FLEET_LOCALES, }) {
    const { theme } = useTheme();
    useEffect(() => {
        const parent = process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN?.trim();
        const host = window.location.hostname;
        const onParent = !!parent && (host === parent || host.endsWith(`.${parent}`));
        const domain = onParent ? `; Domain=.${parent}` : "";
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        const write = (name, value) => {
            document.cookie = `${name}=${value}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
        };
        if (theme === "light" || theme === "dark")
            write("owlcat-theme", theme);
        else if (theme === "system")
            document.cookie = `owlcat-theme=; Path=/; Max-Age=0; SameSite=Lax${domain}${secure}`;
        const loc = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1];
        if (loc && locales.includes(loc))
            write("owlcat-locale", loc);
    }, [theme, locales]);
    return null;
}
export function ThemeProvider({ children, locales, initialTheme, }) {
    return (_jsxs(NextThemesProvider, { attribute: "class", defaultTheme: initialTheme ?? "system", enableSystem: true, disableTransitionOnChange: true, children: [_jsx(ThemeCookieSync, { locales: locales }), children] }));
}
